import React from 'react';
import { Vector3 } from 'three';
import { Line, Sphere } from '@react-three/drei';

interface PathProps {
  points: Vector3[];
}

const Path: React.FC<PathProps> = ({ points }) => {
  // Create path segments for better visualization
  const createPathSegments = () => {
    const segments = [];
    for (let i = 0; i < points.length - 1; i++) {
      segments.push({
        start: points[i],
        end: points[i + 1],
        isStart: i === 0,
        isEnd: i === points.length - 2
      });
    }
    return segments;
  };

  const pathSegments = createPathSegments();

  return (
    <group>
      {/* Main path line */}
      <Line
        points={points}
        color="#ffff00"
        lineWidth={3}
      />
      
      {/* Glowing path overlay */}
      <Line
        points={points}
        color="#ffffff"
        lineWidth={1}
        transparent
        opacity={0.7}
      />
      
      {/* Path segments with directional arrows */}
      {pathSegments.map((segment, index) => {
        // Calculate midpoint and direction
        const midPoint = new Vector3().addVectors(segment.start, segment.end).multiplyScalar(0.5);
        const direction = new Vector3().subVectors(segment.end, segment.start).normalize();
        
        // Calculate perpendicular vector for arrow
        const perpendicular = new Vector3(-direction.z, 0, direction.x).normalize();
        
        // Arrow points
        const arrowBase = midPoint.clone();
        const arrowLeft = arrowBase.clone().add(
          direction.clone().multiplyScalar(-0.3).add(perpendicular.clone().multiplyScalar(0.3))
        );
        const arrowRight = arrowBase.clone().add(
          direction.clone().multiplyScalar(-0.3).add(perpendicular.clone().multiplyScalar(-0.3))
        );
        const arrowTip = arrowBase.clone().add(direction.clone().multiplyScalar(0.5));
        
        return (
          <group key={`path-segment-${index}`}>
            {/* Direction arrow */}
            <Line
              points={[arrowLeft, arrowBase, arrowRight]}
              color="#ffff00"
              lineWidth={2}
            />
            <Line
              points={[arrowBase, arrowTip]}
              color="#ffff00"
              lineWidth={2}
            />
          </group>
        );
      })}
      
      {/* Path points with elevated markers */}
      {points.map((point, index) => (
        <group key={`path-point-${index}`}>
          {/* Ground marker */}
          <mesh position={point}>
            <sphereGeometry args={[0.3, 16, 16]} />
            <meshStandardMaterial 
              color={index === 0 ? '#00ff00' : index === points.length - 1 ? '#ff0000' : '#ffff00'} 
              emissive={index === 0 ? '#00ff00' : index === points.length - 1 ? '#ff0000' : '#ffff00'}
              emissiveIntensity={0.5}
            />
          </mesh>
          
          {/* Vertical beam for visibility */}
          <mesh position={[point.x, 2, point.z]}>
            <cylinderGeometry args={[0.05, 0.05, 4, 8]} />
            <meshBasicMaterial 
              color={index === 0 ? '#00ff00' : index === points.length - 1 ? '#ff0000' : '#ffff00'} 
              transparent
              opacity={0.3}
            />
          </mesh>
          
          {/* Elevated marker */}
          <mesh position={[point.x, 4, point.z]}>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshBasicMaterial 
              color={index === 0 ? '#00ff00' : index === points.length - 1 ? '#ff0000' : '#ffff00'} 
              transparent
              opacity={0.7}
            />
          </mesh>
        </group>
      ))}
      
      {/* Start and end markers */}
      <group position={[points[0].x, 1, points[0].z]}>
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[2, 0.5, 0.1]} />
          <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
        </mesh>
        <mesh position={[0, 1.5, 0]}>
          <boxGeometry args={[1.5, 0.3, 0.1]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
      </group>
      
      <group position={[points[points.length - 1].x, 1, points[points.length - 1].z]}>
        <mesh position={[0, 1, 0]}>
          <boxGeometry args={[2, 0.5, 0.1]} />
          <meshStandardMaterial color="#ff0000" emissive="#ff0000" emissiveIntensity={0.5} />
        </mesh>
        <mesh position={[0, 1.5, 0]}>
          <boxGeometry args={[1.5, 0.3, 0.1]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
      </group>
    </group>
  );
};

export default Path; 